import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { cn } from '../../utils/cn';
import { ChevronDown } from 'lucide-react';

export interface SelectInputOption {
  value: string;
  label: string;
}

interface SelectInputProps {
  options: SelectInputOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md';
  allowCustomValue?: boolean;
}

const getLabelByValue = (options: SelectInputOption[], value: string): string =>
  options.find((o) => o.value === value)?.label ?? value;

const getHighlightIndex = (
  options: SelectInputOption[],
  search: string,
  customOptionAtEnd?: boolean
): number => {
  if (!search.trim()) return 0;
  const lower = search.trim().toLowerCase();
  const i = options.findIndex((o) => o.label.toLowerCase() === lower);
  if (i >= 0) return i;
  if (customOptionAtEnd && options.length > 0) return options.length - 1;
  return 0;
};

const matchByLabel = (
  options: SelectInputOption[],
  search: string
): SelectInputOption | undefined =>
  options.find((o) => o.label.toLowerCase() === search.trim().toLowerCase());

const SIZE_CLASSES = {
  sm: { input: 'px-2 py-1 rounded-md text-sm', label: 'text-xs' },
  md: { input: 'px-3 py-2 rounded-lg text-sm', label: 'text-sm' },
} as const;

const SelectInput: React.FC<SelectInputProps> = ({
  options,
  value = '',
  onChange,
  onBlur,
  placeholder = 'Select or type...',
  label,
  error,
  required = false,
  disabled = false,
  className,
  size = 'md',
  allowCustomValue = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() =>
    getLabelByValue(options, value)
  );
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const scrollFromKeyboardRef = useRef(false);

  const hasOptions = options.length > 0;
  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(inputValue.trim().toLowerCase())
  );
  const exactMatch = matchByLabel(options, inputValue);
  const showUseCustomOption =
    allowCustomValue && inputValue.trim() !== '' && !exactMatch;
  const useCustomOption = useMemo(
    () =>
      showUseCustomOption
        ? { value: inputValue.trim(), label: inputValue.trim() }
        : null,
    [showUseCustomOption, inputValue]
  );
  const displayOptions = useMemo(
    () =>
      useCustomOption ? [...filteredOptions, useCustomOption] : filteredOptions,
    [filteredOptions, useCustomOption]
  );

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    setInputValue(getLabelByValue(options, value));
  }, [value, options]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        close();
        if (
          !allowCustomValue &&
          getLabelByValue(options, value) !== inputValue
        ) {
          const m = matchByLabel(options, inputValue);
          if (m) onChange?.(m.value);
          else setInputValue(getLabelByValue(options, value));
        }
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [allowCustomValue, value, inputValue, options, onChange, close]);

  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(
        getHighlightIndex(displayOptions, inputValue, showUseCustomOption)
      );
    }
  }, [isOpen, inputValue, displayOptions, showUseCustomOption]);

  useEffect(() => {
    if (
      !scrollFromKeyboardRef.current ||
      highlightedIndex < 0 ||
      !listRef.current
    )
      return;
    const el = listRef.current.children[highlightedIndex] as HTMLElement;
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    scrollFromKeyboardRef.current = false;
  }, [highlightedIndex]);

  const openWithHighlight = (index: number) => {
    setIsOpen(true);
    setHighlightedIndex(index);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setInputValue(next);
    if (allowCustomValue) onChange?.(next);
    openWithHighlight(0);
  };

  const handleInputFocus = () => {
    openWithHighlight(
      getHighlightIndex(displayOptions, inputValue, showUseCustomOption)
    );
  };

  const handleSelect = (opt: SelectInputOption) => {
    setInputValue(opt.label);
    onChange?.(opt.value);
    close();
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        openWithHighlight(0);
      }
      return;
    }

    const n = displayOptions.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        scrollFromKeyboardRef.current = true;
        setHighlightedIndex((i) => (i < n - 1 ? i + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        scrollFromKeyboardRef.current = true;
        setHighlightedIndex((i) => (i > 0 ? i - 1 : n - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < n) {
          handleSelect(displayOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setInputValue(getLabelByValue(options, value));
        setHighlightedIndex(-1);
        close();
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const sizeStyle = SIZE_CLASSES[size];

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {label && (
        <label
          className={cn(
            'block font-medium text-foreground mb-1',
            sizeStyle.label
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="select-input-list"
          aria-activedescendant={
            highlightedIndex >= 0 && highlightedIndex < displayOptions.length
              ? `select-input-option-${highlightedIndex}`
              : undefined
          }
          className={cn(
            'block w-full border border-border shadow-sm bg-card text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors',
            hasOptions && 'pr-9',
            sizeStyle.input,
            error &&
              'border-destructive focus:ring-destructive focus:border-destructive',
            disabled && 'bg-muted cursor-not-allowed'
          )}
        />
        {hasOptions && (
          <button
            type="button"
            tabIndex={-1}
            className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-muted-foreground pointer-events-none"
            aria-hidden
          >
            <ChevronDown
              className={cn(
                'w-4 h-4 transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </button>
        )}
      </div>

      {isOpen && (hasOptions || showUseCustomOption) && (
        <ul
          id="select-input-list"
          ref={listRef}
          role="listbox"
          className="absolute z-50 w-full mt-1 py-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          {displayOptions.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              No matches
            </li>
          ) : (
            displayOptions.map((opt, i) => (
              <li
                key={opt.value}
                id={`select-input-option-${i}`}
                role="option"
                aria-selected={highlightedIndex === i}
                className={cn(
                  'px-3 py-2 text-sm cursor-pointer text-foreground hover:bg-muted',
                  highlightedIndex === i && 'bg-muted'
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(opt);
                }}
                onMouseEnter={() => setHighlightedIndex(i)}
              >
                {opt === useCustomOption ? `Use "${opt.label}"` : opt.label}
              </li>
            ))
          )}
        </ul>
      )}

      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default SelectInput;
